// Anchored detail popover — the Food module's own instance of the FIG
// cockpit pattern (MarketTooltip / FIG-mkt-popover in FIGRenewalPipeline.js):
// portal to document.body, viewport-aware flip and clamp, touch-aware
// triggering. Deliberately NOT the SwimDash-tooltip pattern — that one is
// hover-only and this must open by tap on an iPhone.
//
// This component owns position and dismissal; content is children. It knows
// nothing about nutrients.

import { useState, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Same detection as FIGRenewalPipeline.js — "(hover: none)" asks the actual
// question ("can this pointer hover"), not the user agent.
export const IS_TOUCH =
  typeof window !== 'undefined' && window.matchMedia?.('(hover: none)')?.matches;

// estHeight drives only the flip decision, exactly as in MarketTooltip; the
// rendered box sizes to its content.
export default function FoodPopover({ anchorEl, open, onClose, estHeight = 240, ariaLabel, children }) {
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorEl) { setPos(null); return; }
    const r = anchorEl.getBoundingClientRect();
    const margin = 12;
    const viewportW = window.innerWidth;

    let placement = 'top';
    let top = r.top - margin;
    if (r.top - estHeight - margin < 0) {
      placement = 'bottom';
      top = r.bottom + margin;
    }
    let left = r.left + r.width / 2;
    // Clamp — the box is at most 300px wide or 90vw, so both edges stay on
    // screen at 390px with the margins this enforces.
    const halfW = Math.min(150, viewportW * 0.45);
    if (left < halfW + margin) left = halfW + margin;
    if (left > viewportW - halfW - margin) left = viewportW - halfW - margin;

    setPos({ top, left, placement });
  }, [open, anchorEl, estHeight]);

  // Dismissal: Esc (the WeekSheet convention); any click outside a
  // [data-food-pop] element (triggers and the popover itself carry the
  // attribute, so a trigger's own click reaches its toggle instead of being
  // eaten here); and any scroll — the box is position: fixed off a rect read
  // once, and a scrolled page would leave it floating over the wrong thing.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    function onDocClick(e) {
      if (e.target instanceof Element && e.target.closest('[data-food-pop]')) return;
      onClose();
    }
    function onScroll() { onClose(); }
    window.addEventListener('keydown', onKey);
    document.addEventListener('click', onDocClick);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, onClose]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      className={`Food-popover Food-popover--${pos.placement}`}
      data-food-pop=""
      role="dialog"
      aria-label={ariaLabel}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        transform: pos.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
      }}
    >
      {children}
    </div>,
    document.body
  );
}
